import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import {
	faArrowUpRightFromSquare,
	faFileLines,
	faGlobe,
	faNewspaper,
} from "@fortawesome/pro-regular-svg-icons";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { Icon } from "@/components";
import { DeepResearchSource } from "@/pages/Private/redux/deepResearch/schema";

const KIND_ICONS = {
	web: faGlobe,
	news: faNewspaper,
	doc: faFileLines,
	linkedin: faLinkedinIn,
} as const;

/**
 * Grace period before the popover closes, so the pointer can travel from the
 * pill onto the list without it disappearing (PREC-3680: it has to "stay stuck
 * so I can actually move onto it").
 */
const CLOSE_DELAY_MS = 250;
const POPOVER_WIDTH = 280;
const VIEWPORT_MARGIN = 8;

const PILL_CLASS =
	"inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-border-light bg-white px-1.5 py-0.5 text-[11px] font-semibold text-text-tertiary hover:border-brand-light hover:text-brand-light";

interface SourcePillsProps {
	sources?: DeepResearchSource[];
	/** Shadow-root-safe portal target for the multi-source popover. */
	portalContainer?: HTMLElement | null;
}

/**
 * The Source affordance of a research fact: a single source links straight out,
 * several collapse into one "Sources · N" pill whose list opens on hover.
 */
export const SourcePills: FunctionComponent<SourcePillsProps> = ({ sources, portalContainer }) => {
	const { t } = useTranslation();
	const triggerRef = useRef<HTMLSpanElement>(null);
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

	const cancelClose = useCallback(() => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
	}, []);

	const open = useCallback(() => {
		cancelClose();

		const rect = triggerRef.current?.getBoundingClientRect();

		if (!rect) {
			return;
		}

		// Fixed viewport coordinates: a portal target inside a shadow root is not
		// the scroll context, so absolute + scrollY would land offscreen.
		setPosition({
			top: rect.bottom + 6,
			left: Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN),
		});
	}, [cancelClose]);

	const scheduleClose = useCallback(() => {
		cancelClose();
		closeTimeoutRef.current = setTimeout(() => setPosition(null), CLOSE_DELAY_MS);
	}, [cancelClose]);

	useEffect(() => cancelClose, [cancelClose]);

	if (!sources?.length) {
		return null;
	}

	if (sources.length === 1) {
		const [source] = sources;

		return (
			<a
				className={PILL_CLASS}
				data-testid="research-source-single"
				href={source.url}
				rel="noreferrer"
				target="_blank"
				title={`${source.title} — ${source.domain}`}
				onClick={(event) => event.stopPropagation()}
			>
				<span>{t("profileTracker.deepResearch.source")}</span>
				<Icon className="h-2.5 w-2.5" icon={KIND_ICONS[source.kind] ?? faGlobe} />
				<Icon className="h-2 w-2" icon={faArrowUpRightFromSquare} />
			</a>
		);
	}

	// z-1600: the extension renders the lead panel at z-1500 (`isZHigher`) and portals every
	// popover into the same container, i.e. as a sibling of the panel in one stacking context.
	// At z-1400 the list opened but was painted behind the panel (PREC-4024) — 1600 is the tier
	// the extension's other panel popovers (overflow menu) already use.
	const list = position && (
		<div
			className="z-[1600] flex w-[280px] flex-col gap-0.5 rounded-lg border border-border-light bg-white p-1.5 shadow-lg"
			data-testid="research-source-list"
			style={{ position: "fixed", top: position.top, left: position.left }}
			onMouseEnter={cancelClose}
			onMouseLeave={scheduleClose}
		>
			{sources.map((source) => (
				<a
					key={source.url}
					className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs text-text hover:bg-background-light"
					href={source.url}
					rel="noreferrer"
					target="_blank"
					onClick={(event) => event.stopPropagation()}
				>
					<Icon className="h-3 w-3 shrink-0" icon={KIND_ICONS[source.kind] ?? faGlobe} />
					<span className="min-w-0 flex-1 truncate">{source.title}</span>
					<span className="shrink-0 text-[11px] font-semibold text-text-tertiary">
						{source.domain}
					</span>
				</a>
			))}
		</div>
	);

	return (
		<span
			ref={triggerRef}
			className={PILL_CLASS}
			data-testid="research-source-multi"
			onMouseEnter={open}
			onMouseLeave={scheduleClose}
		>
			<span>{t("profileTracker.deepResearch.sources")}</span>
			<span className="rounded border border-border-light px-1 text-[10px] leading-4">
				{sources.length}
			</span>
			{list && createPortal(list, portalContainer ?? document.body)}
		</span>
	);
};
